import { z } from 'zod';
import {
  DateTimeTzSchema,
  GenericAccountId,
  GenericConnectionId,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericToolId,
  GenericUserID,
  GenericUUID,
} from '../models.generic';
import { EmbeddedToolLightSchema } from '../models.tools';

export const IdentityMetadataSchema = z.object({
  /** What role does this user have in the external system? */
  external_role: z.coerce.string().nullable().optional(),
});

/**
 * Identity Model.
 * @note Updated on April 30th to match existing `identities` db schema,
 *       ... which we can change if we feel strongly about it.
 * @todo Fix duplication of this vs `models.identities.ts`
 */
export const IdentityWriteSchema = z.object({
  /**
   * URI reference to the identity (canonical API URL)
   *
   * **REQUIRED:** This should be the canonical API URL for the user in the external system.
   * Used for relationship building and identity resolution across resources.
   *
   * **Supported formats:**
   * - Standard API URLs: `https://api.github.com/users/username`
   * - TIC URIs: `tic://provider/identities/id` or `tic://synthetic/provider/identities/id`
   * - Test URIs: `test:user_123` (for testing only)
   *
   * @example uri: `https://api.github.com/users/${user.login}`
   * @example uri: `tic://trello/identities/${user.id}` or `tic://synthetic/trello/identities/${user.id}`
   */
  uri: z
    .string()
    .refine(
      uri => {
        // Accept either a valid URL, a TIC URI, or `test:` URIs for testing purposes
        return uri.startsWith('http') || uri.startsWith('tic://') || uri.startsWith('test:');
      },
      { message: 'Must be a valid URL or TIC URI (tic://...)' }
    )
    .openapi({
      example: 'https://api.github.com/users/t1mmen',
      description:
        'The URI to the identity on the external service (can be a standard URL or TIC URI like tic://provider/identities/id)',
    }),
  /**
   * The unique, stable identifier from the external provider
   *
   * **IMPORTANT:** Use the provider's permanent user ID, not username.
   * This should never change over time and be unique within the provider.
   * Can be string or number depending on the provider's ID format.
   *
   * **Use `null` only when creating identity stubs** that will be enriched later.
   *
   * @example external_user_id: user.id // GitHub: number
   * @example external_user_id: user.id // Trello: string
   */
  external_user_id: z
    .string()
    .or(z.number())
    .nullable()
    .openapi({ example: '192263', description: 'The external user id on this `tool_id`' }),
  /**
   * Users' full name from the external system
   *
   * **IMPORTANT:** Use `null` if external API doesn't provide full name.
   * Don't construct names from username or email - only use API-provided values.
   *
   * @example external_name: user.full_name || user.display_name || null
   */
  external_name: z.string().nullable().openapi({
    example: 'Timm Stokke',
    description: 'The external users name on origin `tool_id`',
  }),

  /**
   * Users' username/handle from the external system
   *
   * **IMPORTANT:** Use `null` if external API doesn't provide username.
   * This may change over time and differs from external_user_id.
   *
   * @example external_username: user.login || user.username || null
   */
  external_username: z.string().nullable().openapi({
    example: 't1mmen',
    description: 'The external username on origin `tool_id`',
  }),

  /**
   * Users' verified email address from the external system
   *
   * **IMPORTANT:** Use `null` if email is not available or not verified.
   * Many APIs require special permissions to access email addresses.
   *
   * @example external_email: user.email || null
   */
  external_email: z.string().email().nullable().openapi({
    example: 'timm@stokke.me',
    description: 'The external email address on origin `tool_id`',
  }),
  /**
   * Users' timezone from the external system
   *
   * **IMPORTANT:** Use `null` if timezone is not available from the API.
   * Don't guess or use system timezone - only use API-provided values.
   *
   * @example external_timezone: user.time_zone || null
   */
  external_timezone: z
    .string()
    .nullable()
    .openapi({ description: 'External timezone', example: 'America/Vancouver' }),

  /**
   * Users' avatar/profile image URL from the external system
   *
   * **IMPORTANT:** Use `null` if no avatar is available.
   * Ensure the URL is publicly accessible and doesn't require authentication.
   *
   * @example avatar_url: user.avatar_url || null
   */
  avatar_url: z.string().url().nullable().openapi({
    description: 'The external users avatar URL on origin `tool_id`',
    example: 'https://avatars.githubusercontent.com/u/123456?v=4',
  }),

  /**
   * Can we trust this identity is confirmed/verified on the 3rd party system?
   *
   * **IMPORTANT:** Only set to `true` if the external provider explicitly marks
   * the account as verified (e.g., GitHub verified badge, email confirmation).
   * Use `false` as default when verification status is unknown.
   *
   * This helps determine if we can make associations based on email address.
   *
   * @example verified: user.verified || false
   */
  verified: z.coerce.boolean().default(false),
  metadata: IdentityMetadataSchema.optional(),
  /**
   * The _external_ timestamp of when this identity was last changed.
   *
   * **IMPORTANT:** Use `null` if the external API doesn't provide last modified time.
   * Database will use `NOW()` as fallback. Using artificial "now" timestamps
   * will cause unnecessary re-sync to Timely on every change.
   *
   * This is stored on identity_permissions for TIC Diff reasons.
   *
   * @example last_changed_at: toDateTimeISOrNull(user.updated_at)
   */
  last_changed_at: DateTimeTzSchema.nullable().default(null).openapi({
    description: 'The _external_ timestamp of when this identity was last changed.',
  }),
  /**
   * Opt-in SHA-256 hex hash of the identity's meaningful content (TT-4934).
   *
   * When supplied and differing from the stored hash, the identity is treated
   * as having critical changes and `identity_permissions.last_changed_at` is
   * bumped. Absent means the existing critical-field logic (external_email,
   * external_name, avatar_url, verified) is used (backward compatible).
   *
   * Compute with `computeContentHash()` from the framework utils.
   *
   * @see apps/integration-cloud/src/integrations/utils/compute-content-hash.ts
   * @see https://linear.app/timelyapp/issue/TT-4934/
   */
  content_hash: z
    .string()
    .nullable()
    .optional()
    .describe('Opt-in content hash for change detection (TT-4934)'),
  /** Special instructions to RPC/storage functions
   * @warning Made optional _for now_ since existing integrations may break
   *          .. but this is something we should enforce as nullable.
   * @ref ResourceWriteBaseSchema._storage_instructions
   */
  _storage_instructions: z
    .object({ _auto_subscribe: z.coerce.boolean().optional().default(false) })
    .nullable()
    .optional(),
});
export type IdentityWrite = z.infer<typeof IdentityWriteSchema>;
export type MinimalIdentityWrite = Pick<IdentityWrite, 'uri' | 'external_user_id'>;
/** Writing identities to provider storage requires injecting some relationships: */
export type IdentityWriteToProviderStorage = z.infer<typeof IdentityWriteToProviderStorageSchema>;
export const IdentityWriteToProviderStorageSchema = IdentityWriteSchema.extend({
  tool_id: GenericToolId,
  connection_id: GenericConnectionId,
  account_id: GenericAccountId,
  user_id: GenericUserID.optional().describe(
    '_ONLY EVER_ set if user owning the identity is the authenticated user/writer to db'
  ),
});

export const IdentityReadSchema = IdentityWriteSchema.extend({
  id: GenericUUID.openapi({ description: 'The TIC identity ID' }),
  connection_id: GenericConnectionId,
  tool_id: GenericToolId.openapi({
    description: 'The TIC `tool.id` that this identity belongs to',
  }),
  user_id: GenericUserID.openapi({
    description: 'The TIC `user.id` that has claimed this identity',
  }),
  created_at: DateTimeTzSchema,
  updated_at: DateTimeTzSchema,
  // deleted_at: z.string().datetime({ offset: true }).nullable(),
});
export type IdentityRead = z.infer<typeof IdentityReadSchema>;

/**
 * Presentable identity permissions context (admin/debug browsing).
 *
 * @remarks
 * This is derived from the `identity_permissions` table, not `identities`.
 * It intentionally includes Timely linkage and "should sync" context.
 */
export const PresentableIdentityPermissionsSchema = z.object({
  /** Identity permission row id (for debugging / tracing). */
  id: GenericUUID.describe('Identity permissions row id'),
  /** Whether this identity should sync to Timely for the given account/connection. */
  sync_to_timely: z.boolean(),
  /** Timely `account.id` this identity maps to (if known). */
  timely_account_id: GenericTimelyAccountId.nullable(),
  /** Timely `user.id` this identity maps to (if known). */
  timely_user_id: GenericTimelyUserId.nullable(),
  /** External provider timestamp of last change (used for diffing). */
  last_changed_at: DateTimeTzSchema.nullable(),
  created_at: DateTimeTzSchema,
  updated_at: DateTimeTzSchema,
});
export type PresentableIdentityPermissions = z.infer<typeof PresentableIdentityPermissionsSchema>;

/**
 * Presentable Identity Summary Schema
 *
 * @remarks
 * Used for connection-scoped "identity browsing" (admin/debug style).
 * Supports unclaimed identities (`user_id=null`).
 */
export const PresentableIdentitySummarySchema = IdentityReadSchema.omit({
  user_id: true,
  connection_id: true,
}).extend({
  /**
   * The TIC `user.id` that has claimed this identity (nullable for unclaimed).
   *
   * @remarks
   * For browsing endpoints this is sourced from `identity_permissions.user_id`,
   * which is the account/connection-scoped claim context.
   */
  user_id: GenericUserID.nullable(),
  /** Scope pin for future account-wide browsing (derived from connection context). */
  account_id: GenericAccountId,
  /** Connection this identity was imported under (required for browse). */
  connection_id: GenericConnectionId,
  /** Permission/context details attached to this identity. */
  identity_permissions: PresentableIdentityPermissionsSchema,
  /** Embedded tool info for UI/debug display. */
  tool: EmbeddedToolLightSchema,
});
export type PresentableIdentitySummary = z.infer<typeof PresentableIdentitySummarySchema>;
